import { loadStripe } from "@stripe/stripe-js";
import { AccountType, ClaimStatusEnum } from "./enums";

export const NO_VALUE_PLACEHOLDER = "—";
export const NO_NAME_PLACEHOLDER = "No Name";
export const NO_ACCESS_TOKEN_MESSAGE = "No access token";
export const COMMA_SEPARATOR = ",";
export const VALUE_PER_SPLIT_SEPARATOR = "/";
export const ISO_DATE_TIME_SPLITTER = "T";
export const DATE_SPLITTER = "-";
export const DEFAULT_TIME = "12:00:01.000Z";
export const DEFAULT_COUNTRY = { id: 225, text: "United Kingdom" };
export const REDIRECT_TIMEOUT_MS = 2 * 60000;
export const NON_EXISTING_EMAIL_DOMAIN = "@non.existing.com";
export const DATE_YEARS = 12;
export const ZERO_CURRENCY = "0.00";
export const EMPTY_FIELD_VALUE = "";
export const DOT_CHAR = ".";
export const RECENT_ACTIVITIES_COUNT = 5;
export const EMPTY_VALUE = "";

export const DATE_FORMATS = {
    DATE_FORMAT: "dd/MM/yyyy",
    DATEPICKER_FORMAT: "dd/MM/yyyy",
    CARD_FORMAT: "MM/yy",
    FILTER_FORMAT: "yyyy-MM-dd",
    TIME_FORMAT: "HH:mm",
    CHAT_HEADER: "d MMMM y",
    LAST_LOGIN_DATE: "HH:mm dd/MM/yyyy",
    DATE_TIME_FORMAT: "dd/MM/yyyy HH:mm",
};

export const CURRENCY_FORMAT = {
    thousandSeparator: true,
    isNumericString: true,
    prefix: "£",
    decimalScale: 2,
    fixedDecimalScale: true,
};

export const PRESENT_FORMAT = {
    thousandSeparator: true,
    isNumericString: true,
    suffix: "%",
    decimalScale: 2,
    fixedDecimalScale: false,
};

export const ACCOUNT_TYPE_NAMES: Record<number, string> = {
    [AccountType.Admin]: "account-types.admin",
    [AccountType.Agent]: "account-types.agent",
    [AccountType.PrivateLandlord]: "account-types.private_landlord",
    [AccountType.CorporateLandlord]: "account-types.corporate_landlord",
    [AccountType.Tenant]: "account-types.tenant",
    [AccountType.None]: "account-types.none",
};

export const VALIDATION_RULES = {
    CENTRAL_PHONE_FORMAT: /^((\+[0-9-( )]{3,15})|([0-9-( )]{3,15}))$/,
    CENTRAL_PHONE_MAX_LENGTH: 15,
    INTL_PHONE_NUMBER: /^\+[1-9]\d{0,14}/,
    PHONE_VERIFICATION_CODE_FORMAT: "^[1-9]{1}\\s{1}\\d{1}\\s{1}\\d{1}\\s{1}\\d{1}$",
    ESCAPE_SPECIAL_SYMBOLS: /^[a-zA-Z0-9.() /-]+$/gi,
    VERIFICATION_CODE_RANGE: {
        MIN: 1000,
        MAX: 9999,
    },
    DEFAULT_INPUT_MAX_LENGTH: 255,
    DEFAULT_INPUT_MIN_LENGTH: 0,
    EVIDENCE_COMMENT_INPUT_MAX_LENGTH: 1000,
    RELEASE_REQUEST_CANCEL_REASON_MAX_LENGTH: 1000,
    CLAIM_REASON_COMMENT_INPUT_MIN_LENGTH: 5,
    CLAIM_REASON_COMMENT_INPUT_MAX_LENGTH: 1000,
    CLAIM_SETTLEMENT_COMMENT_MIN_LENGTH: 1,
    CLAIM_SETTLEMENT_COMMENT_MAX_LENGTH: 1000,
    ROLE_DESCRIPTION_MAX_LENGTH: 1000,
    DEACTIVATION_REASON_MAX_LENGTH: 1000,
    SETTLEMENT_MAX_TOTAL_DEDUCTION: 100_000,
    SETTLEMENT_COMMENT_MAX_LENGTH: 1500,
    SETTLEMENT_COMMENT_MIN_LENGTH: 5,
    CASE_NOTE_TEXT_MAX_LENGTH: 6_000_000,
    CASE_NOTE_TEXT_MIN_LENGTH: 1,
    MISSING_DOCUMENT_REASON_MAX_LENGTH: 1500,
    MISSING_DOCUMENT_REASON_MIN_LENGTH: 5,
    PROPOSE_REASON_MAX_LENGTH: 1000,
    PHONE_FORMAT: "^07\\d{3}\\s{1}\\d{6}$",
    OPTIONAL_PHONE_FORMAT: "(^07\\d{3}\\s{0,1}\\d{6}$|^$)",
    EMAIL_FORMAT: /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9-_]+(\.[a-zA-Z0-9-]+)+$/gi,
    COMPANY_REGISTRATION_NUMBER_FORMAT: /^[A-Za-z\d]+$/,
    COMPANY_REGISTRATION_NUMBER_MAX_LENGTH: 10,
    MIN_DATE: new Date(1900, 0, 1),
    MIN_AGE: 16,
    MIN_ROLES_COUNT: 1,
    MAX_ROLES_COUNT: 20,
    VOUCHER_SETTINGS_FIELD_MIN_VALUE: 1,
    TRIM_NO_TAGS_FORMAT: "<[a-zA-Z0-9][^/]*?>.*?</[a-zA-Z0-9]*>|<[a-zA-Z0-9].*?/>",
    DELETE_USER_REASON_MIN_LENGTH: 5,
    DELETE_USER_REASON_MAX_LENGTH: 1000,
};

export const INTERCOM_SETTINGS = {
    isIntercomIdExist: Boolean(window.APP_SETTINGS.intercomClientId),
    isIntercomEnabledForInternalUser: Boolean(window.APP_SETTINGS.enableIntercomChatForInternalUser),
};

export const FILTER_CONSTANTS = {
    CHECKED: "checked",
};

export const INVOICE_FILE = {
    FILE_NAME: "Ome invoice-",
    FILE_EXTENSION: ".pdf",
};

export const DEFAULT_COUNTRY_UK_ID = 225;

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_VARIANTS = [10, 20, 50, 100];

export const DEFAULT_DEBOUNCE_INTERVAL = 1000;

export const MAXIMUM_VOUCHERS_COUNT = 9999999;
export const MAXIMUM_TENANCY_DETAILS_POUNDS_FLOAT = 9999999.98;
export const MAXIMUM_TENANCY_DETAILS_POUNDS_STRING = "9,999,999.99";
export const STAR = "*";

export const CHARACTERS_COUNT_BEFORE_TRUNCATE = 300;

export const ACCOUNT_TYPES: number[] = [
    AccountType.Admin,
    AccountType.PrivateLandlord,
    AccountType.CorporateLandlord,
    AccountType.Tenant,
    AccountType.Agent,
    AccountType.None,
];

export const MultilineTextFieldDefaults = {
    DEFAULT_ROWS: 2,
    MAX_ROWS: 10,
};

export const DEFAULT_COUNTRY_KEY = "gb";
export const PREFERRED_COUNTRIES: string[] = ["gb"];

const DOC_TYPES = ".doc,.docx,.odt";
const EXCEL_TYPES = ".xls,.xlsx,.ods";
export const CSV_TYPES = ".csv";
const TXT_TYPES = ".txt";
export const PDF_TYPES = "application/pdf";
const DOC_TYPES_FOR_CLAIM_EVIDENCES = ".doc,.docx";

export const IMAGES_MIME_TYPES = "image/*";
export const VIDEOS_TYPES =
    ".webm,.mpg,.mp2,.mp1,.mpeg,.mpe,.mpv,.ogg,.mp4,.m4p,.m4v,.avi,.wmv,.mov,.qt,.flv,.swg,.avchd,.mkv";
export const SUPPORTED_IMAGES_TYPES =
    ".apng,.avif,.gif,.jpg,.jpeg,.jfif,.pjpeg,.pjp,.jpe,.png,.svg,.webp,.ico,.cur,.bmp";

export const ALLOWED_AVATAR_TYPES = ".jpg,.jpeg,.gif,.png";
export const ALLOWED_DOCUMENTS_TYPES = `${PDF_TYPES},${IMAGES_MIME_TYPES},${VIDEOS_TYPES},${DOC_TYPES},${EXCEL_TYPES}`;
export const ALLOWED_EVIDENCES_TYPES = `${ALLOWED_DOCUMENTS_TYPES}`;
export const ALLOWED_CLAIM_EVIDENCES_TYPES = `${DOC_TYPES_FOR_CLAIM_EVIDENCES},${PDF_TYPES},${IMAGES_MIME_TYPES}`;
export const ALLOWED_BULK_UPLOAD_TYPES = `${CSV_TYPES},${TXT_TYPES}`;
export const BULK_UPLOAD_ERROR_LOG_FILENAME = "errorLog.csv";

export const ALLOWED_EVIDENCES_FILE_SIZE = 256 * 1024 * 1024;

export const THUMBNAIL = {
    WIDTH: 168,
    HEIGHT: 168,
    LOAD_DELAY_MS: 3000,
    LOAD_TIME_TO_TRY: 180 * 1000,
    LOAD_MAX_TRIES: 60,
};

export const CHAT_PAGE_SIZE = 50;
export const CHAT_GROUP_SPLIT_ON_DELAY_MS = 5 * 60 * 1000;

export const GALLERY_START_SLIDER = 1;
export const GALLERY_SLIDE_SPACING = 8;

export const PAGE_HAS_BEEN_FORCE_REFRESHED_KEY = "pageHasBeenForceRefreshed";

export const DEFAULT_CHART_SETTINGS = {
    tooltip: {
        enabled: false,
    },

    legend: {
        show: false,
    },
};

export enum CompanyType {
    Agency = 1,
    CompanyLandlord = 2,
}

export const POLLING_INTERVAL_BULK_UPLOAD_MS = 10000;

export const ZERO_VALUE = 0;

export const stripePromise = loadStripe(window.SYSTEM_SETTINGS.stripePublicKey);

export const PUBLIC_FILES_FOLDER_PATH = `${window.location.origin}/files`;
export const BULK_UPLOAD_PUBLIC_FILE_TEMPLATE = `${PUBLIC_FILES_FOLDER_PATH}/bulk_upload_template.csv`;

export const CLAIM_CLOSED_STATUSES = [
    ClaimStatusEnum.ClosedDeclined,
    ClaimStatusEnum.ClosedAccepted,
    ClaimStatusEnum.ClosedAcceptedPartially,
];

export const RECENT_ACTIVITIES_ITEMS_VIEW_MAX = 5;

export const TENANCY_RECALCULATE_TRADITIONAL_DEPOSIT_TIMEOUT_MS = 500;

export const TENANCY_START_DATE_MIN_YEARS_FROM_NOW = -1;
export const TENANCY_START_DATE_MAX_YEARS_FROM_NOW = 2;
export const TENANCY_START_DATE_MAX_MONTHS_FROM_NOW = 24;
export const END_OF_TENANCY_LEAVING_DATE_MAX_MONTHS_FROM_NOW = 12;

export const DEFAULT_NOTIFICATION_ROW_HEIGHT_PX = 85;
export const NOTIFICATIONS_LIST_MAX_HEIGHT_PX = 656;
export const NOTIFICATIONS_LIST_MIN_HEIGHT_PX = 150;

export const DISABLE_SIGN_UP = true;
